import { IDENTITY_MATRIX, type Rect } from '../core/geometry';
import { createId, createLayerBase, type RasterLayer, type VectorLayer } from '../core/model';
import { canonicalizeRegions } from '../core/selection';
import type { PathNode, PathObject } from './types';

export interface ImageTraceOptions {
  threshold: number;
  invert: boolean;
  maxSamples?: number;
  maxRegions?: number;
}

function pixelAt(layer: RasterLayer, x: number, y: number): readonly [number, number, number, number] {
  const tileX = Math.floor(x / layer.raster.tileSize);
  const tileY = Math.floor(y / layer.raster.tileSize);
  const pixels = layer.raster.getTile(tileX, tileY);
  if (!pixels) return [0, 0, 0, 0];
  const dimensions = layer.raster.tileDimensions(tileX, tileY);
  const localX = x - tileX * layer.raster.tileSize;
  const localY = y - tileY * layer.raster.tileSize;
  const index = (localY * dimensions.width + localX) * 4;
  return [pixels[index] ?? 0, pixels[index + 1] ?? 0, pixels[index + 2] ?? 0, pixels[index + 3] ?? 0];
}

export function traceRasterRegions(layer: RasterLayer, options: ImageTraceOptions): { regions: Rect[]; sampleScale: number } | null {
  const maximum = Math.max(1, options.maxSamples ?? 4_000_000);
  const sampleScale = Math.max(1, Math.ceil(Math.sqrt(layer.raster.width * layer.raster.height / maximum)));
  const threshold = Math.max(0, Math.min(255, options.threshold));
  const regions: Rect[] = [];
  for (let y = 0; y < layer.raster.height; y += sampleScale) {
    let start: number | null = null;
    for (let x = 0; x <= layer.raster.width; x += sampleScale) {
      let selected = false;
      if (x < layer.raster.width) {
        const [red, green, blue, alpha] = pixelAt(layer, x, y);
        const luminance = red * 0.2126 + green * 0.7152 + blue * 0.0722;
        selected = alpha > 0 && (options.invert ? luminance >= threshold : luminance < threshold);
      }
      if (selected && start === null) start = x;
      if (!selected && start !== null) {
        regions.push({ x: start, y, width: Math.min(layer.raster.width, x) - start, height: Math.min(sampleScale, layer.raster.height - y) });
        start = null;
        if (regions.length > (options.maxRegions ?? 50_000) * 64) return null;
      }
    }
  }
  const canonical = canonicalizeRegions(regions);
  return canonical.length > (options.maxRegions ?? 50_000) ? null : { regions: canonical, sampleScale };
}

function regionsPath(regions: readonly Rect[], sourceId: string, options: ImageTraceOptions, sampleScale: number): PathObject | null {
  if (!regions.length) return null;
  const nodes: PathNode[] = [];
  const subpaths: PathObject['geometry']['subpaths'][number][] = [];
  for (const region of regions) {
    const subpathId = createId('subpath');
    const anchors = [
      { x: region.x, y: region.y },
      { x: region.x + region.width, y: region.y },
      { x: region.x + region.width, y: region.y + region.height },
      { x: region.x, y: region.y + region.height },
    ];
    const additions = anchors.map((anchor): PathNode => ({ id: createId('node'), subpathId, anchor, incoming: null, outgoing: null, continuity: 'corner' }));
    nodes.push(...additions);
    subpaths.push({ id: subpathId, nodeIds: additions.map((node) => node.id), closed: true });
  }
  return {
    id: createId('vector'),
    name: 'Image Trace',
    visible: true,
    opacity: 1,
    transform: IDENTITY_MATRIX,
    fill: { type: 'solid', color: '#111111', alpha: 1 },
    stroke: null,
    extensions: { 'maipo:image-trace': { sourceLayerId: sourceId, threshold: options.threshold, invert: options.invert, sampleScale } },
    type: 'path',
    geometry: { nodes, subpaths, fillRule: 'nonzero' },
  };
}

export function traceRasterLayer(layer: RasterLayer, options: ImageTraceOptions): VectorLayer | null {
  const traced = traceRasterRegions(layer, options);
  if (!traced) return null;
  const path = regionsPath(traced.regions, layer.id, options, traced.sampleScale);
  if (!path) return null;
  return {
    ...createLayerBase(`${layer.name} trace`),
    kind: 'vector',
    transform: layer.transform,
    vector: { schemaVersion: 2, objects: [path], extensions: {} },
  };
}
